import React, { useState } from 'react';
import { uploadTaxDocument } from '../services/firestoreService';
import { DOCUMENT_TYPE_GROUPS } from '../documentTypes';
import { useAuth } from '../context/AuthContext';

interface CaseDocumentUploaderProps {
  caseId: string;
  currentUserUid: string;
  isIdFolder?: boolean;
}

const CaseDocumentUploader: React.FC<CaseDocumentUploaderProps> = ({ caseId, currentUserUid, isIdFolder = false }) => {
  const { firmId } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError('');
    }
  };

  const handleUpload = async () => {
    // Fix: pass firmId to uploadTaxDocument
    if (!file || !category || !firmId) return;
    setIsUploading(true);
    setError('');
    try {
      // Fix: pass firmId as second argument
      await uploadTaxDocument(file, firmId, caseId, category, currentUserUid);
      setFile(null);
      setCategory('');
    } catch (err) {
      console.error("Upload error:", err);
      setError("Failed to upload document. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm h-full">
      <div className="mb-4">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          Upload to {isIdFolder ? 'ID Folder' : 'Tax Return'}
        </h3>
        <p className="text-sm text-slate-500">Securely upload files to this {isIdFolder ? 'permanent' : 'year\'s'} records.</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Document Type *</label>
          <select 
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-white font-medium"
            disabled={isUploading}
            required
          >
            <option value="" disabled>-- Select Document Type --</option>
            {DOCUMENT_TYPE_GROUPS.map((group) => (
              <optgroup key={group.groupName} label={group.groupName}>
                {group.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center transition-colors ${
          file ? 'border-brand-300 bg-brand-50' : 'border-slate-300 hover:bg-slate-50'
        }`}>
          <input 
            type="file" 
            id="doc-upload" 
            className="hidden" 
            onChange={handleFileChange}
            accept=".pdf,.jpg,.png,.doc,.docx"
            disabled={isUploading}
          />
          
          {!file ? (
             <label htmlFor="doc-upload" className="cursor-pointer w-full flex flex-col items-center">
                <span className="text-3xl text-slate-300 mb-2">☁️</span>
                <span className="text-brand-600 font-medium text-sm hover:underline">Choose a file or drag and drop</span>
                <span className="text-xs text-slate-400 mt-1">PDF, JPG, PNG up to 10MB</span>
             </label>
          ) : (
            <div className="flex flex-col items-center w-full">
                <span className="text-sm font-semibold text-brand-800 break-all">{file.name}</span>
                <span className="text-xs text-brand-600 mb-3">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                <button 
                    onClick={() => setFile(null)}
                    disabled={isUploading}
                    className="text-xs text-red-500 hover:text-red-700 font-medium px-3 py-1 bg-white rounded border border-red-100 shadow-sm"
                >
                    Remove
                </button>
            </div>
          )}
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <button
            onClick={handleUpload}
            disabled={!file || !category || isUploading}
            className={`w-full py-2.5 rounded-lg font-bold text-white transition-all shadow-sm flex items-center justify-center gap-2 ${
                !file || !category || isUploading 
                ? 'bg-slate-300 cursor-not-allowed' 
                : 'bg-brand-600 hover:bg-brand-700 active:scale-95'
            }`}
        >
            {isUploading ? 'Encrypting & Uploading...' : 'Secure Upload'}
        </button>
      </div>
    </div>
  );
};

export default CaseDocumentUploader;
